import React, { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { DataTable } from '@/components/shared/data-table/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { GET_PRODUCT_PRICING_DATA } from '../productQueries'

interface PriceList {
  id: number
  name: string
}

interface BasePrice {
  id: number
  price: number
  price_from?: number
  price_listId: number
  price_list: PriceList
}

interface FabricCategoryPrice {
  id: number
  price: number
  price_listId: number
  price_list: PriceList
  fabrice_price_category: {
    id: number
    group_number: number
  }
}

interface SofaForm {
  id: number
  name: string
  code: string
  form_price_fabric_category: FabricCategoryPrice[]
}

interface ComponentPrice {
  extra_prices: {
    id: number
    price: number
    price_listId: number
    price_list: PriceList
  }[]
  price_fabric_category: {
    id: number
    price: number
    price_listId: number
    price_list: PriceList
    fabrice_price_category: {
      id: number
      group_number: number
    }
  }[]
  additional_component: {
    id: number
    code: string
    additional_component_profiles: {
      id: number
      name: string
      language: string
    }[]
    additional_component_group?: {
      id: number
      use_fabric_prices_for_components: boolean
    } | null
  }
}

interface ComponentTableData {
  id: string
  code: string
  name: string
  price_fabric_category: {
    price: number
    fabrice_price_category: {
      group_number: number
    }
  }[]
}

interface ExtraPriceTableData {
  id: string
  code: string
  name: string
  price: number
}

interface SofaModuleTableData {
  id: string
  code: string
  name: string
  form_price_fabric_category: {
    price: number
    fabrice_price_category: {
      group_number: number
    }
  }[]
}

interface ProductPricelistsCardProps {
  productId: number
  language: string
}

// Define columns for additional components table (fabric category pricing).
// The 'code' column header doubles as the section title bar (see the
// `[&_thead_th]:bg-primary` wrapper around the DataTable) and 'name' is left
// blank so the dark bar reads as one continuous row.
const additionalComponentsColumns: ColumnDef<ComponentTableData>[] = [
  {
    accessorKey: 'code',
    header: () => <div className="whitespace-nowrap">Fabric Category Pricing</div>,
  },
  {
    accessorKey: 'name',
    header: () => <span className="sr-only">Component Name</span>,
  },
  {
    accessorKey: 'cat_1',
    header: () => <div className="text-right">CAT 1</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 1
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_2',
    header: () => <div className="text-right">CAT 2</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 2
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_3',
    header: () => <div className="text-right">CAT 3</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 3
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_4',
    header: () => <div className="text-right">CAT 4</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 4
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_5',
    header: () => <div className="text-right">CAT 5</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 5
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_6',
    header: () => <div className="text-right">CAT 6</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 6
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_7',
    header: () => <div className="text-right">CAT 7</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 7
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_L1',
    header: () => <div className="text-right">L1</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 11
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_L2',
    header: () => <div className="text-right">L2</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 12
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
]

// Define columns for extra prices table (flat pricing, no fabric category
// columns). 'code' carries the section title bar text, 'name' stays blank.
const extraPricesColumns: ColumnDef<ExtraPriceTableData>[] = [
  {
    accessorKey: 'code',
    header: () => <div className="whitespace-nowrap">Fixed Pricing</div>,
  },
  {
    accessorKey: 'name',
    header: () => <span className="sr-only">Component Name</span>,
  },
  {
    accessorKey: 'price',
    header: () => <div className="text-right">Price</div>,
    cell: ({ row }) => (
      <div className="text-right">€{row.getValue('price')}</div>
    ),
  },
]

// Define columns for sofa modules table. Sofa forms always carry fabric
// category pricing (SofaForm has no use_fabric_prices_for_components-style
// flag), so this table always shows the CAT columns.
const sofaModulesColumns: ColumnDef<SofaModuleTableData>[] = [
  {
    accessorKey: 'code',
    header: () => <div className="whitespace-nowrap">Module/Form Prices</div>,
  },
  {
    accessorKey: 'name',
    header: () => <span className="sr-only">Module Name</span>,
  },
  {
    accessorKey: 'cat_1',
    header: () => <div className="text-right">CAT 1</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 1
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_2',
    header: () => <div className="text-right">CAT 2</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 2
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_3',
    header: () => <div className="text-right">CAT 3</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 3
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_4',
    header: () => <div className="text-right">CAT 4</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 4
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_5',
    header: () => <div className="text-right">CAT 5</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 5
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_6',
    header: () => <div className="text-right">CAT 6</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 6
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_7',
    header: () => <div className="text-right">CAT 7</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 7
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_L1',
    header: () => <div className="text-right">L1</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 11
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
  {
    accessorKey: 'cat_L2',
    header: () => <div className="text-right">L2</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 12
        )?.price || '-'
      return (
        <div className="text-right">{price !== '-' ? `€${price}` : price}</div>
      )
    },
  },
]

export function ProductPricelistsCard({
  productId,
  language,
}: ProductPricelistsCardProps) {
  const [pricingDataLoaded, setPricingDataLoaded] = useState(false)

  const [loadPricingData, { data, loading, error }] = useLazyQuery(
    GET_PRODUCT_PRICING_DATA,
    {
      variables: { id: productId },
      onCompleted: () => setPricingDataLoaded(true),
    }
  )

  const handleLoadPrices = () => {
    loadPricingData()
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price)
  }

  const getComponentName = (
    component: ComponentPrice['additional_component']
  ) => {
    const profile = component.additional_component_profiles.find(
      p => p.language.toLowerCase() === language.toLowerCase()
    )
    return (
      profile?.name ||
      component.additional_component_profiles[0]?.name ||
      component.code
    )
  }

  if (!pricingDataLoaded && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricelists</CardTitle>
          <p className="text-sm text-gray-600">
            Click the button below to load pricing information for this product.
          </p>
        </CardHeader>
        <CardContent>
          <Button onClick={handleLoadPrices} className="w-full">
            Get All Prices
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricelists</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading pricing data...</span>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricelists</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-red-500 mb-4">
              Error loading pricing data: {error.message}
            </p>
            <Button onClick={handleLoadPrices} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const pricingData = data?.findUniqueProductContainer?.advanced_product
  if (!pricingData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricelists</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">
            No pricing data available for this product.
          </p>
        </CardContent>
      </Card>
    )
  }

  const basePrices = pricingData.base_prices || []
  const fabricCategoryPrices =
    pricingData.advanced_product_price_fabric_category || []
  const sofaForms = pricingData.sofa_forms || []
  const componentPrices =
    pricingData.additional_component_to_advanced_product || []

  // Collect all unique pricelists
  const allPricelists = new Map<number, PriceList>()

  // Collect pricelists from all sources
  basePrices.forEach(price => {
    if (price.price_list) {
      allPricelists.set(price.price_list.id, price.price_list)
    }
  })

  fabricCategoryPrices.forEach(price => {
    if (price.price_list) {
      allPricelists.set(price.price_list.id, price.price_list)
    }
  })

  sofaForms.forEach(form => {
    form.form_price_fabric_category?.forEach(price => {
      if (price.price_list) {
        allPricelists.set(price.price_list.id, price.price_list)
      }
    })
  })

  componentPrices.forEach(component => {
    component.extra_prices?.forEach(price => {
      if (price.price_list) {
        allPricelists.set(price.price_list.id, price.price_list)
      }
    })
    component.price_fabric_category?.forEach(price => {
      if (price.price_list) {
        allPricelists.set(price.price_list.id, price.price_list)
      }
    })
  })

  const uniquePricelists = Array.from(allPricelists.values())

  if (uniquePricelists.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricelists</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">
            No pricelist associations found for this product.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Associated Pricelists</CardTitle>
          <p className="text-sm text-gray-600">
            This product is included in {uniquePricelists.length} pricelist
            {uniquePricelists.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={handleLoadPrices} variant="outline" size="sm">
          <Loader2
            className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : 'hidden'}`}
          />
          Refresh Prices
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {uniquePricelists.map(pricelist => {
          const relevantBasePrices = basePrices.filter(
            p => p.price_listId === pricelist.id
          )
          const relevantFabricPrices = fabricCategoryPrices.filter(
            p => p.price_listId === pricelist.id
          )
          const relevantSofaForms = sofaForms.filter(form =>
            form.form_price_fabric_category?.some(
              p => p.price_listId === pricelist.id
            )
          )
          const relevantComponents = componentPrices.filter(
            component =>
              component.extra_prices?.some(
                p => p.price_listId === pricelist.id
              ) ||
              component.price_fabric_category?.some(
                p => p.price_listId === pricelist.id
              )
          )

          return (
            <div key={pricelist.id} className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">{pricelist.name}</h3>
                <Badge variant="outline">ID: {pricelist.id}</Badge>
              </div>

              {/* Base Prices */}
              {relevantBasePrices.length > 0 && (
                <div>
                  <div className="bg-primary text-primary-foreground font-bold px-3 py-2 rounded-md mb-2">
                    Base Product Price
                  </div>
                  <div className="space-y-1">
                    {relevantBasePrices.map(price => (
                      <div
                        key={price.id}
                        className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded"
                      >
                        <span className="text-sm">Base Price</span>
                        <div className="text-right">
                          {price.price_from &&
                          price.price_from !== price.price ? (
                            <span className="text-sm font-medium">
                              {formatPrice(price.price_from)} -{' '}
                              {formatPrice(price.price)}
                            </span>
                          ) : (
                            <span className="text-sm font-medium">
                              {formatPrice(price.price)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fabric Category Prices */}
              {relevantFabricPrices.length > 0 && (
                <div>
                  <div className="bg-primary text-primary-foreground font-bold px-3 py-2 rounded-md mb-2">
                    Fabric Category Prices
                  </div>
                  <div className="space-y-1">
                    {relevantFabricPrices.map(price => (
                      <div
                        key={price.id}
                        className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded"
                      >
                        <span className="text-sm">
                          Fabric Group{' '}
                          {price.fabrice_price_category.group_number}
                        </span>
                        <span className="text-sm font-medium">
                          {formatPrice(price.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sofa Form Prices */}
              {relevantSofaForms.length > 0 && (
                <div>
                  {(() => {
                    // Transform sofa forms data for table display
                    const sofaModulesData: SofaModuleTableData[] =
                      relevantSofaForms.map(form => {
                        const formPrices =
                          form.form_price_fabric_category?.filter(
                            p => p.price_listId === pricelist.id
                          ) || []

                        return {
                          id: form.id.toString(),
                          code: form.code,
                          name: form.name,
                          form_price_fabric_category: formPrices,
                        }
                      })

                    return (
                      <div className="[&_thead_th]:bg-primary [&_thead_th]:!text-primary-foreground [&_thead_th]:!font-bold [&_thead_tr]:hover:bg-transparent">
                        <DataTable
                          columns={sofaModulesColumns}
                          data={sofaModulesData}
                          defaultPageSize={10}
                        />
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Component Prices */}
              {relevantComponents.length > 0 && (
                <div>
                  {(() => {
                    // Separate components by pricing type
                    const categoryComponents: ComponentTableData[] = []
                    const extraPriceComponents: ExtraPriceTableData[] = []

                    relevantComponents.forEach((component, index) => {
                      const componentName = getComponentName(
                        component.additional_component
                      )
                      const componentCode =
                        component.additional_component?.code || '-'
                      const componentId =
                        component.additional_component?.id || 'unknown'

                      // Whether this component's group actually prices by
                      // fabric category. This is the authoritative signal
                      // (the same flag the "Use Fabric Prices for
                      // Components" toggle writes in
                      // AdditionalComponentGroupForm.tsx) -- we don't infer
                      // it from whether price_fabric_category rows happen to
                      // be present, since stale/leftover rows from a group
                      // that used to be fabric-priced would otherwise still
                      // force CAT columns to render. Only a strict `false`
                      // is treated as "no category prices"; a missing group
                      // (null/undefined) fails open to category pricing.
                      const isFlatPriced =
                        component.additional_component?.additional_component_group
                          ?.use_fabric_prices_for_components === false

                      // Components with fabric category pricing
                      const fabricPrices =
                        component.price_fabric_category?.filter(
                          p => p.price_listId === pricelist.id
                        ) || []

                      if (!isFlatPriced && fabricPrices.length > 0) {
                        categoryComponents.push({
                          id: `${componentId}_fabric_${index}`,
                          code: componentCode,
                          name: componentName,
                          price_fabric_category: fabricPrices || [],
                        })
                      }

                      // Components with extra pricing (no categories)
                      const extraPrices =
                        component.extra_prices?.filter(
                          p => p.price_listId === pricelist.id
                        ) || []

                      extraPrices.forEach((extraPrice, priceIndex) => {
                        extraPriceComponents.push({
                          id: `${componentId}_extra_${index}_${priceIndex}`,
                          code: componentCode,
                          name: componentName,
                          price: extraPrice.price || 0,
                        })
                      })
                    })

                    return (
                      <div className="space-y-6">
                        {categoryComponents.length > 0 && (
                          <div className="[&_thead_th]:bg-primary [&_thead_th]:!text-primary-foreground [&_thead_th]:!font-bold [&_thead_tr]:hover:bg-transparent">
                            <DataTable
                              columns={additionalComponentsColumns}
                              data={categoryComponents}
                              defaultPageSize={10}
                            />
                          </div>
                        )}

                        {extraPriceComponents.length > 0 && (
                          <div className="[&_thead_th]:bg-primary [&_thead_th]:!text-primary-foreground [&_thead_th]:!font-bold [&_thead_tr]:hover:bg-transparent">
                            <DataTable
                              columns={extraPricesColumns}
                              data={extraPriceComponents}
                              defaultPageSize={10}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}

              {relevantBasePrices.length === 0 &&
                relevantFabricPrices.length === 0 &&
                relevantSofaForms.length === 0 &&
                relevantComponents.length === 0 && (
                  <p className="text-sm text-gray-500 italic">
                    No specific pricing details available for this pricelist.
                  </p>
                )}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
